import { AlertCircle, Brain, Check, User, Wrench } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { EventEntry } from "@/api/types";

interface MessageProps {
  event: EventEntry;
  onOpenFile: (path: string, name: string) => void;
}

export function SessionMessage({ event, onOpenFile }: MessageProps) {
  if (event.type === "user_prompt") {
    return (
      <UserPrompt
        text={event.prompt as string | undefined}
        timestamp={event.timestamp}
      />
    );
  }

  if (event.type === "assistant") {
    return <AssistantMessage event={event} onOpenFile={onOpenFile} />;
  }

  if (event.type === "user") {
    // tool_result comes through as "user" with tool_result content — render
    // as a small inline marker unless it's error-flagged.
    return <ToolResultRow event={event} />;
  }

  if (event.type === "result") {
    return <ResultRow event={event} />;
  }

  if (event.type === "timeline_divergence" || event.type === "system") {
    return null;
  }

  return null;
}

function UserPrompt({
  text,
  timestamp,
}: {
  text: string | undefined;
  timestamp: string | undefined;
}) {
  if (!text) return null;
  return (
    <div
      className="flex items-start gap-2 animate-fade-in"
      style={{ animationDuration: "180ms" }}
    >
      <User
        size={12}
        className="mt-1.5 shrink-0"
        style={{ color: "var(--t3)" }}
      />
      <div className="min-w-0 flex-1 flex flex-col items-start">
        <div
          className="rounded-lg px-3 py-2 max-w-full"
          style={{
            background: "color-mix(in srgb, var(--accent) 6%, transparent)",
            border:
              "1px solid color-mix(in srgb, var(--accent) 12%, transparent)",
            color: "var(--t1)",
            fontSize: "13.5px",
            lineHeight: 1.5,
            fontFamily: "var(--font-body)",
          }}
        >
          {text.slice(0, 2000)}
        </div>
        <MessageTime iso={timestamp} className="mt-0.5 ml-1" />
      </div>
    </div>
  );
}

function AssistantMessage({
  event,
  onOpenFile,
}: {
  event: EventEntry;
  onOpenFile: (path: string, name: string) => void;
}) {
  const msg = event.message as Record<string, unknown> | undefined;
  const content = msg?.content;
  if (!content) return null;

  const blocks = Array.isArray(content)
    ? content
    : [{ type: "text", text: String(content) }];

  // Show the timestamp under the message, but skip purely-thinking-only
  // turns — those render as a tiny `<details>` triangle and a stamp
  // hanging off them looks lopsided.
  const hasUserVisibleBlock = blocks.some(
    (b: Record<string, unknown>) =>
      (b.type === "text" && b.text) || b.type === "tool_use",
  );

  return (
    <div
      className="space-y-1.5 animate-fade-in"
      style={{ animationDuration: "180ms" }}
    >
      {blocks.map((block: Record<string, unknown>, i: number) => {
        if (block.type === "text" && block.text) {
          return (
            <div
              key={i}
              className="prose-chat pl-3"
              style={{
                borderLeft:
                  "2px solid color-mix(in srgb, var(--accent) 20%, transparent)",
                fontFamily: "var(--font-body)",
                fontSize: "13.5px",
                lineHeight: 1.6,
                color: "var(--t1)",
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {block.text as string}
              </ReactMarkdown>
            </div>
          );
        }

        if (block.type === "thinking" && block.thinking) {
          const thinking = block.thinking as string;
          return (
            <details key={i} className="group">
              <summary
                className="flex items-center gap-1 cursor-pointer list-none"
                style={{
                  color: "var(--t3)",
                  fontSize: "11px",
                  fontFamily: "var(--font-mono)",
                }}
              >
                <Brain size={10} />
                <span>Thinking ({thinking.length.toLocaleString()} chars)</span>
              </summary>
              <div
                className="pl-3 mt-1 whitespace-pre-wrap"
                style={{
                  borderLeft: "1px solid var(--border)",
                  color: "var(--t3)",
                  fontSize: "11px",
                  lineHeight: 1.5,
                  fontFamily: "var(--font-body)",
                  fontStyle: "italic",
                }}
              >
                {thinking}
              </div>
            </details>
          );
        }

        if (block.type === "tool_use") {
          return (
            <ToolUseRow
              key={i}
              toolName={block.name as string}
              input={block.input as Record<string, unknown>}
              onOpenFile={onOpenFile}
            />
          );
        }

        return null;
      })}
      {hasUserVisibleBlock && (
        <MessageTime iso={event.timestamp} className="pl-3" />
      )}
    </div>
  );
}

function ToolUseRow({
  toolName,
  input,
  onOpenFile,
}: {
  toolName: string;
  input: Record<string, unknown>;
  onOpenFile: (path: string, name: string) => void;
}) {
  const filePath = (input?.file_path ?? input?.path) as string | undefined;
  const command = input?.command as string | undefined;
  const description = input?.description as string | undefined;

  // Normalize sprite paths to desk paths
  const normalizePath = (p: string) =>
    p
      .replace(/^\/home\/sprite\/agent_workspace\/?/, "/")
      .replace(/^\/home\/sprite\/data\/?/, "/");

  const label =
    toolName === "Bash" && command
      ? truncate(command, 80)
      : (description ?? toolName);

  return (
    <div
      className="flex items-start gap-1.5 pl-3"
      style={{
        fontSize: "11px",
        fontFamily: "var(--font-mono)",
        color: "var(--t3)",
      }}
    >
      <Wrench
        size={10}
        className="mt-0.5 shrink-0"
        style={{ color: "var(--warm)" }}
      />
      <div className="flex items-baseline gap-1.5 min-w-0 flex-1">
        <span style={{ color: "var(--t2)" }}>{toolName}</span>
        {filePath && (
          <button
            type="button"
            onClick={() => {
              const rel = normalizePath(filePath);
              const name = rel.split("/").pop() ?? rel;
              onOpenFile(rel, name);
            }}
            className="truncate hover:underline"
            style={{ color: "var(--info)", textAlign: "left" }}
            title={filePath}
          >
            {truncate(filePath, 50)}
          </button>
        )}
        {!filePath && label && (
          <span className="truncate" style={{ color: "var(--t4)" }}>
            {label}
          </span>
        )}
      </div>
    </div>
  );
}

function ToolResultRow({ event }: { event: EventEntry }) {
  const isError = event.is_error === true;
  if (!isError) return null; // successes are implicit; only surface errors
  return (
    <div
      className="flex items-center gap-1 pl-3 text-[11px]"
      style={{ color: "var(--err)", fontFamily: "var(--font-mono)" }}
    >
      <AlertCircle size={10} />
      tool error
    </div>
  );
}

function ResultRow({ event }: { event: EventEntry }) {
  const isError = event.is_error === true || event.subtype === "error";
  const cost = event.total_cost_usd as number | undefined;
  const duration = event.duration_ms as number | undefined;
  return (
    <div
      className="flex items-center gap-2 pl-3 pt-2 text-[11px]"
      style={{
        color: isError ? "var(--err)" : "var(--ok)",
        fontFamily: "var(--font-mono)",
      }}
    >
      {isError ? <AlertCircle size={11} /> : <Check size={11} />}
      <span className="uppercase tracking-wider font-medium">
        {isError ? "Failed" : "Done"}
      </span>
      {duration != null && (
        <span style={{ color: "var(--t4)" }}>
          {(duration / 1000).toFixed(1)}s
        </span>
      )}
      {cost != null && (
        <span style={{ color: "var(--t4)" }}>${cost.toFixed(3)}</span>
      )}
    </div>
  );
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/**
 * Tiny "sent at HH:MM" subscript for chat messages. Tooltip carries the
 * full ISO so the operator-debug case ("which timestamp exactly was
 * this?") is one hover away. Day prefix only when we cross day
 * boundaries — keeps the inline form quiet.
 */
function MessageTime({
  iso,
  className,
}: {
  iso: string | undefined;
  className?: string;
}) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return (
    <time
      dateTime={iso}
      title={iso}
      className={className}
      style={{
        fontSize: "10px",
        fontFamily: "var(--font-mono)",
        color: "var(--t4)",
        lineHeight: 1.2,
        userSelect: "none",
      }}
    >
      {formatMessageTime(d)}
    </time>
  );
}

/**
 * "9:00 AM" for today, "Yesterday 9:00 AM" for yesterday, "May 1 9:00 AM"
 * for this year, "May 1 2025 9:00 AM" before that. Local time always.
 */
function formatMessageTime(d: Date): string {
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  if (sameDay) return time;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isYesterday) return `Yesterday ${time}`;

  const sameYear = d.getFullYear() === now.getFullYear();
  const date = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
  return `${date} ${time}`;
}
